import React from "react";

/**
 * Confirm what the sheet said about each person's pay.
 *
 * ONE ROW PER DISTINCT WORDING, not per person. A 145-row sheet holds perhaps
 * twenty distinct compensation strings, and ruling on twenty is work a person
 * will actually do.
 *
 * THE ORIGINAL CELL IS ALWAYS ON SCREEN
 *
 * This is the rule the whole screen is built around, not a nicety. The figures
 * come from a model reading prose - "INR 1.6 CR fixed + 35lakh bonus + ESOPS =
 * INR 2.2CR" - and a wrong reading is only harmless when the reader can see it
 * is wrong. With the cell beside the number, checking is a glance. Without it,
 * nobody would know.
 *
 * NOTHING IS CONFIRMED BY DEFAULT
 *
 * Every row starts ignored. A figure counts only when someone has ticked it,
 * and a row with no currency cannot be ticked at all - a number without a
 * currency is not an amount.
 */

const ACCENT = "rgb(var(--primary-rgb, 210, 105, 30))";

/** The units the compensation form offers, with what each multiplies by. The
 *  same list compensationParser.js keeps, because the two have to agree. */
const UNITS = {
  Crore: 10000000,
  Lakhs: 100000,
  Million: 1000000,
  Thousand: 1000,
  None: 1,
};

const GRID = "minmax(220px, 2fr) 54px repeat(3, 92px) 104px 96px 56px";

/**
 * A row that needs nothing further from the user: it has a currency and at
 * least one figure, so it could be recorded exactly as it stands.
 *
 * Exported because ImportFromExcel decides the opening tick state with it, and
 * the screen disables the tick with it. One rule, two users - if they drifted
 * apart a row could arrive ticked and then refuse to be unticked.
 */
export const usableReading = (row) =>
  Boolean(row.currency) &&
  (row.fixed !== null || row.variable !== null || row.total !== null);

/** Below this the model flagged a problem with its own reading. */
const SHAKY = 5;

function CompensationReview({
  readings,
  currencies,
  onChange,
  // Set while the next step is being prepared. The screen stays where it is
  // during that call, so the only place the wait can show is the button.
  continuing,
  onBack,
  onContinue,
  onClose,
}) {
  const sheetNames = Object.keys(readings || {});

  const rows = sheetNames.flatMap((name) =>
    (readings[name] || []).map((row) => ({ ...row, sheet: name })),
  );

  const confirmed = rows.filter((row) => !row.ignored);

  /** A row can only be confirmed once it has a currency and some figure. */
  const usable = usableReading;

  // Complete rows arrive ticked, so the ones the model was least sure of can
  // now be ticked without anyone having looked at them. They sort to the top
  // and their score is red, but the footer says it outright as well.
  const shakyKept = confirmed.filter((row) => row.confidence < SHAKY).length;

  const set = (sheet, value, patch) => {
    onChange(sheet, value, patch);
  };

  // No loading state. This screen is only rendered once the readings are in
  // hand - the wait shows in the Continue button on the screen before it.
  return (
    <>
      <div
        className="flex-shrink-0 d-flex align-items-start gap-3"
        style={{ padding: "20px 26px 18px", borderBottom: "1px solid #eeeae5" }}
      >
        <div>
          <div style={{ fontSize: "19px", fontWeight: 700, color: "#1c1917" }}>
            Check the compensation
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px", color: "#78716b" }}>
            What your sheet said on the left, what Pine would record on the
            right.
          </div>
          <div
            style={{
              marginTop: "4px",
              fontSize: "12.5px",
              color: ACCENT,
              fontWeight: 600,
            }}
          >
            Rows that need nothing from you are already ticked. Check each
            figure against the wording beside it — the least certain are at the
            top — and untick anything that is wrong.
          </div>
        </div>

        {/* Every step carries one. Nothing is written from this screen, so
            leaving costs the figures ticked here and nothing already in PINE. */}
        {onClose && (
          <button
            type="button"
            className="btn-close ms-auto"
            aria-label="Close"
            onClick={() => onClose()}
          />
        )}
      </div>

      <div
        className="flex-grow-1"
        style={{ minHeight: 0, overflowY: "auto", padding: "16px 26px" }}
      >
        {rows.length === 0 && (
          <div style={{ fontSize: "13px", color: "#78716b" }}>
            Nothing could be read from that column.
          </div>
        )}

        {sheetNames.map((name) => (
          <div key={name} style={{ marginBottom: "22px" }}>
            <div
              style={{
                fontSize: "12.5px",
                fontWeight: 700,
                color: "#44403c",
                marginBottom: "8px",
              }}
            >
              {name} · {(readings[name] || []).length} wording
              {(readings[name] || []).length === 1 ? "" : "s"}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: GRID,
                gap: "10px",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: "#8a837c",
                paddingBottom: "7px",
                borderBottom: "1px solid #eeeae5",
              }}
            >
              <div>YOUR SHEET SAID</div>
              <div>PEOPLE</div>
              <div>FIXED</div>
              <div>VARIABLE</div>
              <div>TOTAL</div>
              <div>UNIT</div>
              <div>CURRENCY</div>
              <div>KEEP</div>
            </div>

            {(readings[name] || []).map((row) => {
              const canKeep = usable(row);

              return (
                <div
                  key={row.value}
                  style={{
                    display: "grid",
                    gridTemplateColumns: GRID,
                    gap: "10px",
                    alignItems: "start",
                    padding: "10px 0",
                    borderBottom: "1px solid #f4f1ed",
                  }}
                >
                  {/* The cell, verbatim. Line breaks inside it are kept - they
                      are how the consultant separated cash from stock, and
                      flattening them hides the thing being checked. */}
                  <div>
                    <div
                      style={{
                        fontSize: "12.5px",
                        color: "#1c1917",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {row.value}
                    </div>

                    {row.note && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: row.confidence >= 5 ? "#78716b" : "#b91c1c",
                          marginTop: "2px",
                        }}
                      >
                        {row.note}
                      </div>
                    )}

                    {row.people.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {row.people.map((person, i) => (
                          <span
                            key={i}
                            style={{
                              fontSize: "10.5px",
                              color: "#57534e",
                              background: "#f5f3f0",
                              borderRadius: "4px",
                              padding: "1px 6px",
                            }}
                          >
                            {person.person || `row ${person.number}`}
                          </span>
                        ))}
                        {row.rows > row.people.length && (
                          <span style={{ fontSize: "10.5px", color: "#a8a29b" }}>
                            + {row.rows - row.people.length} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: "12.5px", color: "#78716b" }}>
                    {row.rows}
                  </div>

                  {["fixed", "variable", "total"].map((field) => (
                    <input
                      key={field}
                      type="number"
                      step="any"
                      className="form-control form-control-sm"
                      style={{ fontSize: "12px" }}
                      value={row[field] === null ? "" : row[field]}
                      onChange={(e) =>
                        set(name, row.value, {
                          [field]:
                            e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  ))}

                  <select
                    className="form-select form-select-sm"
                    style={{ fontSize: "12px" }}
                    value={row.unit}
                    onChange={(e) =>
                      set(name, row.value, {
                        unit: e.target.value,
                        multiplier: UNITS[e.target.value],
                      })
                    }
                  >
                    {Object.keys(UNITS).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>

                  {/* Empty when the cell did not say, and the row cannot be
                      kept until it is filled. "400k" is not an amount. */}
                  <select
                    className="form-select form-select-sm"
                    style={{ fontSize: "12px" }}
                    value={row.currency || ""}
                    onChange={(e) =>
                      set(name, row.value, {
                        currency: e.target.value || null,
                      })
                    }
                  >
                    <option value="">— pick —</option>
                    {currencies.map((currency) => (
                      <option key={currency.id} value={currency.name}>
                        {currency.name}
                      </option>
                    ))}
                  </select>

                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input mt-0"
                      checked={!row.ignored}
                      disabled={!canKeep}
                      title={
                        canKeep
                          ? "Keep this figure"
                          : "Needs a currency and an amount"
                      }
                      onChange={(e) =>
                        set(name, row.value, { ignored: !e.target.checked })
                      }
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color:
                          row.confidence >= 5 ? "#3f6b46" : "#b91c1c",
                      }}
                    >
                      {row.source === "model" ? row.confidence : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div
        className="flex-shrink-0 d-flex align-items-center gap-3"
        style={{
          padding: "14px 22px",
          borderTop: "1px solid #eeeae5",
          background: "#fff",
        }}
      >
        <div style={{ fontSize: "12.5px", color: "#78716b" }}>
          {confirmed.length} of {rows.length} kept
          {confirmed.length === 0 ? " — nothing will be recorded" : ""}
          {shakyKept > 0 && (
            <span style={{ color: "#b91c1c", fontWeight: 600 }}>
              {" "}
              · {shakyKept} of them the model was unsure about — those are at
              the top
            </span>
          )}
        </div>

        <div className="ms-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onBack}
          >
            Back
          </button>
          {/* Never blocked. Keeping nothing is a legitimate answer - the import
              carries on exactly as it does today. */}
          <button
            type="button"
            className="btn btn-primary"
            style={{ background: ACCENT }}
            disabled={continuing}
            onClick={onContinue}
          >
            {continuing && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
                style={{ verticalAlign: "-2px" }}
              />
            )}
            {continuing ? "Checking PINE..." : "Continue"}
          </button>
        </div>
      </div>
    </>
  );
}

export default CompensationReview;
